import React, { useMemo, useState } from "react";
import { Cloud, Eye, EyeOff, X } from "lucide-react";
import { http, errMsg } from "@/lib/api";
import { Section, Field } from "@/components/storage/ConnectModalPrimitives";

/**
 * ConnectS3Modal — first-class Amazon S3 (or S3-compatible: MinIO, Wasabi,
 * Backblaze B2) connection UX, matching ConnectStorageModal's NAS flow.
 *
 * Built new: no prior UI anywhere ever collected real S3 credentials --
 * Agents.jsx's "Add manually" modal only ever captured bucket/region/
 * prefix/credential_ref (a vault-path reference, never a secret), so an S3
 * connection record created there has no way to actually authenticate.
 *
 * Pass `editAgent` (an existing S3 agent doc) to edit in place instead of
 * creating a new one -- fields pre-fill from it, Save PATCHes
 * /agents/{id}/storage/s3. Secret fields are never pre-filled (the server
 * never sends them back); leaving them blank on save keeps the currently
 * stored credential unchanged.
 */
export default function ConnectS3Modal({ role = "source", editAgent = null, onClose, onSaved }) {
    const isEdit = !!editAgent;
    const initialS3 = editAgent?.s3 || {};
    const hadCredential = !!initialS3.has_credential;
    const hadSessionToken = !!initialS3.has_session_token;

    const [name, setName] = useState(editAgent?.name || "");
    const [bucket, setBucket] = useState(initialS3.bucket || "");
    const [region, setRegion] = useState(initialS3.region || "");
    const [prefix, setPrefix] = useState(initialS3.prefix || "");
    const [endpoint, setEndpoint] = useState(initialS3.endpoint || "");
    const [accessKeyId, setAccessKeyId] = useState(initialS3.access_key_id || "");
    const [secretAccessKey, setSecretAccessKey] = useState("");
    const [sessionToken, setSessionToken] = useState("");
    const [showSecret, setShowSecret] = useState(false);

    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const displayName = useMemo(() => {
        if (name.trim()) return name.trim();
        return bucket || "";
    }, [name, bucket]);

    const save = async () => {
        setErr("");
        if (!bucket) {
            setErr("Please fill in the bucket name");
            return;
        }
        if (!displayName) {
            setErr("Please name this connection");
            return;
        }
        if (!isEdit && (!accessKeyId || !secretAccessKey)) {
            setErr("Access key ID and secret access key are required");
            return;
        }
        setBusy(true);
        try {
            const s3 = {
                bucket,
                region: region || null,
                prefix: prefix || null,
                endpoint: endpoint || null,
                access_key_id: accessKeyId || null,
                // Blank on an edit means "keep the stored credential" -- the
                // server never sends secrets back, so these fields only
                // ever carry a REPLACEMENT the operator just typed.
                secret_access_key: secretAccessKey || null,
                session_token: sessionToken || null,
            };
            if (isEdit) {
                const { data } = await http.patch(`/agents/${editAgent.id}/storage/s3`, { name: displayName, s3 });
                onSaved(data);
            } else {
                const { data } = await http.post("/agents", {
                    name: displayName, role, kind: "s3", version: "1.0.0-s3", bucket, s3,
                });
                onSaved(data);
            }
        } catch (e) {
            setErr(errMsg(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog">
            <div
                className="w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-xl border border-border bg-surface shadow-2xl"
                data-testid="connect-s3-modal"
            >
                <div className="flex items-center justify-between border-b border-border px-5 py-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-[#FF9900] to-[#e88a00] text-white shadow-md">
                            <Cloud size={16} />
                        </div>
                        <div>
                            <div className="font-display text-lg font-bold">
                                {isEdit ? "Edit S3 Connection" : "Connect Amazon S3"}
                            </div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                {isEdit ? editAgent.name : (role === "source" ? "Source S3 bucket" : "Destination S3 bucket")}
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md border border-border p-1.5 hover:border-foreground/40"
                        data-testid="connect-s3-close"
                        aria-label="Close"
                    >
                        <X size={13} />
                    </button>
                </div>

                <div className="space-y-4 p-5">
                    <Section title="Bucket">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <Field label="Bucket name" required>
                                <input
                                    value={bucket}
                                    onChange={(e) => setBucket(e.target.value)}
                                    placeholder="my-company-backups"
                                    data-testid="s3-bucket-input"
                                    className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
                                />
                            </Field>
                            <Field label="Region">
                                <input
                                    value={region}
                                    onChange={(e) => setRegion(e.target.value)}
                                    placeholder="us-east-1"
                                    data-testid="s3-region-input"
                                    className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
                                />
                            </Field>
                        </div>
                        <div className="mt-3">
                            <Field label="Prefix (optional path within the bucket)">
                                <input
                                    value={prefix}
                                    onChange={(e) => setPrefix(e.target.value)}
                                    placeholder="backups/"
                                    data-testid="s3-prefix-input"
                                    className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
                                />
                            </Field>
                        </div>
                        <div className="mt-3">
                            <Field label="Custom endpoint (S3-compatible providers only — MinIO, Wasabi, Backblaze B2)">
                                <input
                                    value={endpoint}
                                    onChange={(e) => setEndpoint(e.target.value)}
                                    placeholder="Leave blank for real AWS S3"
                                    data-testid="s3-endpoint-input"
                                    className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
                                />
                            </Field>
                        </div>
                    </Section>

                    <Section title="Credentials">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <Field label="Access key ID" required={!isEdit}>
                                <input
                                    value={accessKeyId}
                                    onChange={(e) => setAccessKeyId(e.target.value)}
                                    placeholder="AKIA..."
                                    autoComplete="off"
                                    data-testid="s3-access-key-input"
                                    className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
                                />
                            </Field>
                            <Field label="Secret access key" required={!isEdit}>
                                <div className="relative">
                                    <input
                                        type={showSecret ? "text" : "password"}
                                        value={secretAccessKey}
                                        onChange={(e) => setSecretAccessKey(e.target.value)}
                                        placeholder={isEdit ? "Leave blank to keep current" : "••••••••"}
                                        autoComplete="new-password"
                                        data-testid="s3-secret-key-input"
                                        className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 pr-9 font-mono text-xs"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowSecret((s) => !s)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                                        aria-label={showSecret ? "Hide secret" : "Show secret"}
                                        data-testid="s3-secret-toggle"
                                        tabIndex={-1}
                                    >
                                        {showSecret ? <EyeOff size={13} /> : <Eye size={13} />}
                                    </button>
                                </div>
                                {isEdit && (
                                    <div className="mt-1 text-[10px] text-muted-foreground" data-testid="s3-secret-edit-hint">
                                        {hadCredential
                                            ? "A secret access key is already stored — only fill this in to replace it."
                                            : "No secret access key is currently stored."}
                                    </div>
                                )}
                            </Field>
                        </div>
                        <div className="mt-3">
                            <Field label="Session token (optional — for temporary STS credentials)">
                                <input
                                    type={showSecret ? "text" : "password"}
                                    value={sessionToken}
                                    onChange={(e) => setSessionToken(e.target.value)}
                                    placeholder={isEdit && hadSessionToken ? "Leave blank to keep current" : "Only needed for STS temporary credentials"}
                                    autoComplete="off"
                                    data-testid="s3-session-token-input"
                                    className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
                                />
                            </Field>
                        </div>
                    </Section>

                    <Section title="Display name">
                        <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={displayName || "e.g. Production Backups (S3)"}
                            data-testid="s3-name-input"
                            className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        />
                    </Section>

                    {err && (
                        <div className="rounded-md border border-[hsl(var(--destructive))]/50 bg-[hsl(var(--destructive))]/5 px-3 py-2 text-xs text-[hsl(var(--destructive))]">
                            {err}
                        </div>
                    )}
                </div>

                <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-border bg-surface px-5 py-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md border border-border px-3 py-2 text-xs font-semibold uppercase tracking-widest"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        disabled={busy || !bucket}
                        data-testid="s3-save-btn"
                        className="rounded-md bg-gradient-to-r from-[#FF9900] to-[#e88a00] px-4 py-2 text-xs font-semibold uppercase tracking-widest text-white shadow-md hover:shadow-lg disabled:opacity-60"
                    >
                        {isEdit ? (busy ? "Saving…" : "Save Changes") : (busy ? "Connecting…" : "Connect S3")}
                    </button>
                </div>
            </div>
        </div>
    );
}
